import React from 'react';
import Close from '@mui/icons-material/Close';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

export default function Chip(theme: Theme) {
  return {
    MuiChip: {
      defaultProps: {
        deleteIcon: <Close />
      },

      styleOverrides: {
        root: {
          borderRadius: 100, // full pill
          fontWeight: 500,
          transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease'
        },
        sizeSmall: {
          height: 22,
          fontSize: '0.75rem'
        },
        sizeMedium: {
          height: 28,
          fontSize: '0.8125rem'
        },
        // Default filled chip — neutral unselected
        filled: {
          backgroundColor: theme.palette.grey[200],
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.grey[300]
          },
          // Sana.ai style: alpha tint bg + main color text (both from .main, lighter feel)
          '&.MuiChip-colorPrimary': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.16)
            }
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
            color: theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.secondary.main, 0.16)
            }
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main
          },
          // Warning: keep dark text — .main (#FFC107) has poor contrast on light yellow
          '&.MuiChip-colorWarning': {
            backgroundColor: alpha(theme.palette.warning.main, 0.14),
            color: theme.palette.warning.dark
          },
          '&.MuiChip-colorError': {
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            color: theme.palette.info.main
          }
        },
        // Outlined — clean border, no fill
        outlined: {
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.grey[400]
          },
          '&.MuiChip-colorPrimary': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08)
            }
          },
          '&.MuiChip-colorSecondary': {
            borderColor: theme.palette.secondary.main,
            color: theme.palette.secondary.main
          }
        },
        colorDefault: {
          '& .MuiChip-avatarMedium, .MuiChip-avatarSmall': {
            color: theme.palette.text.secondary
          }
        },
        label: {
          paddingLeft: 10,
          paddingRight: 10
        },
        labelSmall: {
          paddingLeft: 8,
          paddingRight: 8
        }
      }
    }
  };
}
